<template>
  <div class="team-results">
    <div class="e4s-flex-row e4s-justify-flex-row-vert-center e4s-gap--standard">
      <h2 class="e4s-header--400">Team Results</h2>

      <select
        v-model="seriesNameSelected"
        class="browser-default e4s-input-field e4s-input-field--primary e4s-flex-row--end"
        @change="onEgIdFilterChange"
      >
        <option value="">Select Age Group</option>
        <option
          v-for="seriesName in teamResultsState?.seriesNames"
          :key="seriesName"
          :value="seriesName"
        >
          {{ seriesName }}
        </option>
      </select>
    </div>

    <div>
      Top {{ seriesNameDisplayData?.maxAthletesPerTeam }} positions per team required to qualify.
      Click on Day Score to see the athletes.
    </div>

    <div v-if="debugUi">
      {{ teamResultsState?.seriesNameToEgIdsMap }}
    </div>

    <!-- Table Body -->
    <div v-if="hasServiceNameBeenSelected" class="team-results__body">
      <!-- Qualified Teams -->
      <div class="team-results__header e4s-flex-row e4s-gap--standard e4s-align-items-center">
        <div class="team-results__col team-results__col--team">Qualified Teams:</div>

        <!-- Loop the seriesNameDisplayData?.maxAthletesPerTeam and create #1, #2, etc. -->
        <template
          v-for="(teamNames, dateEgKey) in seriesNameDisplayData?.dateBasedKeyAllTeams"
          :key="dateEgKey"
        >
          <div class="team-results__col team-results__col--position">
            {{ formatDateBasedKey(dateEgKey) }}
          </div>
        </template>

        <div class="team-results__col team-results__col--total">Total</div>
      </div>
      <div
        v-for="(seriesClub, index) in seriesNameDisplayData?.series"
        :key="`${seriesClub.clubName}-${seriesClub.egIds.join('-')}`"
        class="team-results__row e4s-flex-row e4s-gap--standard e4s-align-items-center"
        :class="index % 2 === 1 ? 'team-results__row--even' : ''"
      >
        <div class="team-results__col team-results__col--team">
          <span
            :title="'EgIds: ' + seriesClub.egIds.join(', ')"
            class="e4s-lite-table--standard-text"
            >{{ seriesClub.clubName }}</span
          >
          <!-- <span class="team-results__event-group">(EG: {{ seriesClub.egIds.join(', ') }})</span> -->
        </div>
        <div
          v-for="(position, dateEgKey) in seriesNameDisplayData?.dateBasedKeyAllTeams"
          :key="dateEgKey"
          class="team-results__col team-results__col--position"
        >
          <button
            class="team-results__position-button e4s-lite-number e4s-lite-number--position"
            @click="showAthletesDetailsByDateEgKey(seriesClub, dateEgKey)"
          >
            {{ seriesClub.dateTopAthletesMap![dateEgKey] }}
          </button>
        </div>
        <div class="team-results__col team-results__col--total">
          <span class="e4s-lite-number e4s-lite-number--score">{{ seriesClub.totalScore }}</span>
        </div>
      </div>
      <!-- /Qualified Teams -->

      <!-- Non Qualified Teams -->

      <p style="padding-top: 32px; padding-bottom: var(--e4s-gap--standard)"></p>

      <div class="e4s-flex-row e4s-align-items-center e4s-gap--standard">
        <div class="team-results__legend-rectangle"></div>
        <span class="team-results__legend-text"> = Insufficient qualifying athletes</span>
      </div>

      <div class="team-results__header e4s-flex-row e4s-gap--standard e4s-align-items-center">
        <div class="team-results__col team-results__col--team">Non Qualified Teams:</div>

        <!-- Loop the seriesNameDisplayData?.maxAthletesPerTeam and create #1, #2, etc. -->
        <template
          v-for="(teamNames, dateEgKey) in seriesNameDisplayData?.dateBasedKeyAllTeams"
          :key="dateEgKey"
        >
          <div class="team-results__col team-results__col--position">
            {{ formatDateBasedKey(dateEgKey) }}
          </div>
        </template>

        <div class="team-results__col team-results__col--total">Total</div>
      </div>
      <div
        v-for="(seriesClub, index) in seriesNameDisplayData?.seriesNonQualified"
        :key="`${seriesClub.clubName}-${seriesClub.egIds.join('-')}`"
        class="team-results__row e4s-flex-row e4s-gap--standard e4s-align-items-center"
        :class="index % 2 === 1 ? 'team-results__row--even' : ''"
      >
        <div class="team-results__col team-results__col--team">
          <span
            :title="'EgIds: ' + seriesClub.egIds.join(', ')"
            class="e4s-lite-table--standard-text"
            >{{ seriesClub.clubName }}</span
          >
          <!-- <span class="team-results__event-group">(EG: {{ seriesClub.egIds.join(', ') }})</span> -->
        </div>
        <div
          v-for="(position, dateEgKey) in seriesNameDisplayData?.dateBasedKeyAllTeams"
          :key="dateEgKey"
          class="team-results__col team-results__col--position"
          :class="
            isAthleteCountAtLeastCss(
              seriesClub,
              dateEgKey,
              seriesNameDisplayData!.maxAthletesPerTeam
            )
              ? ''
              : 'team-results__row--unqualified'
          "
        >
          <button
            class="team-results__position-button e4s-lite-number e4s-lite-number--position"
            @click="showAthletesDetailsByDateEgKey(seriesClub, dateEgKey)"
          >
            {{ seriesClub.dateTopAthletesMap![dateEgKey] }}
          </button>
        </div>
        <div class="team-results__col team-results__col--total">
          <span class="e4s-lite-number e4s-lite-number--score">{{ seriesClub.totalScore }}</span>
        </div>
      </div>
      <!-- /Non Qualified Teams -->
    </div>

    <!-- Empty State -->
    <div
      v-if="!hasServiceNameBeenSelected && processedTeams.length === 0"
      class="team-results__empty"
    >
      <p class="e4s-lite-table--standard-text">No team results available</p>
    </div>

    <!-- Prompt to select Event Group -->
    <div v-if="!hasServiceNameBeenSelected" class="team-results__empty">
      <p class="e4s-lite-table--standard-text">Please select an Event Group to view team results</p>
    </div>

    <!-- Athlete Details Modal -->
    <div v-if="showModal" class="team-results__modal-overlay" @click="closeModal">
      <div class="team-results__modal" @click.stop>
        <div class="team-results__modal-header">
          <h3 class="e4s-header--400">Athlete Details</h3>
          <button class="team-results__modal-close" @click="closeModal">×</button>
        </div>
        <div class="team-results__modal-content" v-if="selectedAthletes.length > 0">
          <div
            class="team-results__athlete-info"
            v-for="athlete in selectedAthletes"
            :key="athlete.athleteId"
          >
            <div class="team-results__athlete-row">
              <span class="team-results__label">Name:</span>
              <span class="team-results__value">{{ athlete.athleteName }}</span>
            </div>
            <div class="team-results__athlete-row">
              <span class="team-results__label">Club:</span>
              <span class="team-results__value">{{ athlete.club }}</span>
            </div>
            <div class="team-results__athlete-row">
              <span class="team-results__label">Bib No:</span>
              <span class="team-results__value">{{ athlete.bibNo }}</span>
            </div>
            <div class="team-results__athlete-row">
              <span class="team-results__label">Position:</span>
              <span class="team-results__value">{{ athlete.position }}</span>
            </div>
            <div class="team-results__athlete-row">
              <span class="team-results__label">Score:</span>
              <span class="team-results__value">{{ formatScore(athlete.score) }}</span>
            </div>
            <div class="team-results__athlete-row">
              <span class="team-results__label">Team:</span>
              <span class="team-results__value">{{ selectedTeam?.teamName }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="debugUi">
      seriesNameDisplayData {{ seriesNameDisplayData?.teamsByAthleteCount.atLeast }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, reactive, watch } from 'vue'
import {
  type TeamResult,
  type TeamAthleteDetail,
  buildTeamResultsState,
  type TeamResultsState,
  sortTeamsByScore,
  buildSeriesNameDisplayData,
  formatDateBasedKey,
  isAthleteCountAtLeastCss,
  type BuildSeriesNameDisplayDataResult,
  type ClubResultSeries
} from './team-results-service'
import { turnSecondsIntoHumanReadableTime } from '@/components/results/results-service'
import type { StoreCompResultsState } from '../store-comp-results'

const props = defineProps<{
  storeCompResultsState: StoreCompResultsState
}>()

const debugUi = false

const teamResultsState = ref<TeamResultsState | null>(null)
const seriesNameSelected = ref<string>('')

// Modal state
const showModal = ref(false)
const selectedAthlete = ref<TeamAthleteDetail | null>(null)
const selectedAthletes = ref<TeamAthleteDetail[]>([])
const selectedTeam = ref<TeamResult | null>(null)

// Date mapping data structure
const dateMappings = reactive({
  egIdToDate: {} as Record<number, string>,
  dateToEgIds: {} as Record<string, number[]>,
  uniqueDates: [] as string[],
  seriesNames: {} as string[],
  seriesNameSelected: '' as string
})

// Initialize date mappings when competition data changes
watch(
  () => props.storeCompResultsState.competitionOnTheDay,
  (newData) => {
    if (newData) {
      teamResultsState.value = buildTeamResultsState(props.storeCompResultsState)
    }
  },
  { immediate: true }
)

const filteredTeams = computed(() => {
  let teams = [...(teamResultsState.value?.teams.teams || [])]

  // Filter by egId if selected
  if (seriesNameSelected.value) {
    const idsToCheck = teamResultsState.value?.seriesNameToEgIdsMap[seriesNameSelected.value] || []
    console.log('idsToCheck:', idsToCheck)
    teams = teams.filter((team) => idsToCheck.includes(team.egId))
  }

  return teams
})

// Sorted teams

const processedTeams = computed(() => {
  let teams = [...filteredTeams.value]

  teams = sortTeamsByScore(teams)

  return teams
})

const hasServiceNameBeenSelected = computed(() => {
  return seriesNameSelected.value !== ''
})

const seriesNameDisplayData = computed<null | BuildSeriesNameDisplayDataResult>(() => {
  if (!teamResultsState.value) {
    return null
  }

  // Build display data for series names
  /*
    storeCompResultsState: StoreCompResultsState,
  teamResultsState: TeamResultsState,
  seriesName: string
  */
  const seriesNameDisplayData = buildSeriesNameDisplayData(
    props.storeCompResultsState,
    teamResultsState.value,
    seriesNameSelected.value
  )

  return seriesNameDisplayData
})

// Event groups for display
// const eventGroups = computed(() => teamResultsData.value.eventGroups)

// Available dates for filtering
const availableDates = computed(() => {
  return dateMappings.uniqueDates
})

// Get description for egId from rhOptions
// const getEgIdDescription = (egId: number): string => {
//   const descriptions = teamResultsData.value.eventGroupDescriptions
//   return descriptions[egId] || `Event Group ${egId}`
// }

// Format date for display (future implementation)
const formatDate = (date: string): string => {
  // TODO: Format date properly
  return date
}

// Format time/score for display
const formatScore = (score: string): string => {
  // Check if it's a numeric score that represents seconds
  const numericScore = parseFloat(score)
  if (!isNaN(numericScore) && numericScore > 0) {
    // Format as time using the existing function
    return turnSecondsIntoHumanReadableTime(numericScore)
  }
  return score
}

// Modal functions
const showAthleteDetails = (team: TeamResult, athlete: TeamAthleteDetail) => {
  // const athlete = team.athletes[positionIndex]
  if (athlete) {
    selectedAthlete.value = athlete
    selectedTeam.value = team
    showModal.value = true
  }
}

const showAthletesDetails = (seriesClub: ClubResultSeries, index: number) => {
  // get the athletes at the position index in each egIdTopAthletesMap
  const athletesAtPosition: TeamAthleteDetail[] = []
  // Loop through each egIdTopAthletesMap
  for (const egId in seriesClub.egIdTopAthletesMap) {
    const athletes = seriesClub.egIdTopAthletesMap[egId as unknown as number]
    if (athletes[index]) {
      athletesAtPosition.push(athletes[index])
    }
  }
  if (athletesAtPosition.length > 0) {
    selectedAthletes.value = athletesAtPosition
    selectedTeam.value = seriesClub.teams[0]
    showModal.value = true
  }
}

/**
 *
 * @param seriesClub
 * @param dateEgKey     e.g. 2025-10-12T00:00:00_18402
 */
const showAthletesDetailsByDateEgKey = (seriesClub: ClubResultSeries, dateEgKey: string) => {
  // get the athletes at the position index in each egIdTopAthletesMap
  let athletesAtPosition: TeamAthleteDetail[] = []

  // extract Eg Id from dateEgKey
  const key = dateEgKey.split('_')[1].toString()

  // Loop through each egIdTopAthletesMap
  let athletes = seriesClub.egIdTopAthletesMap![key as unknown as number]
  if (athletes) {
    athletesAtPosition = athletes
  }
  // if (athletes[index]) {
  //   athletesAtPosition.push(athletes[index])
  //}

  if (athletesAtPosition.length > 0) {
    selectedAthletes.value = athletesAtPosition
    selectedTeam.value = seriesClub.teams[0]
    showModal.value = true
  }
}

const closeModal = () => {
  showModal.value = false
  selectedAthlete.value = null
  selectedTeam.value = null
}

// Filter change handlers
const onEgIdFilterChange = () => {
  // Filter is applied reactively through computed property
}

const onDateFilterChange = () => {
  // Filter is applied reactively through computed property
}

// Sorting functions
// function sortByTeamName() {
//   if (sortKey.value === 'name') {
//     sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
//   } else {
//     sortKey.value = 'name'
//     sortDirection.value = 'asc'
//   }
// }

// function sortByTotalScore() {
//   if (sortKey.value === 'score') {
//     sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
//   } else {
//     sortKey.value = 'score'
//     sortDirection.value = 'asc'
//   }
// }
</script>

<style scoped>
.team-results {
  width: 100%;
}

.team-results__header {
  padding: var(--e4s-gap--standard) 0;
  background-color: #e9ecef;
  border-bottom: 2px solid #dee2e6;
  font-weight: 600;
  margin-top: var(--e4s-gap--small);
  margin-bottom: var(--e4s-gap--small);
}

.team-results__col {
  display: flex;
  align-items: center;
  padding: var(--e4s-gap--small-qaz);
}

.team-results__col--team {
  flex: 2;
  min-width: 200px;
}

.team-results__col--position {
  flex: 0 0 60px;
  justify-content: center;
}

.team-results__col--total {
  flex: 0 0 100px;
  justify-content: center;
  font-weight: bold;
  /* right align text */
  text-align: right;
}

.team-results__row {
  padding: var(--e4s-gap--small) 0;
  border-bottom: 1px solid #f0f0f0;
}

.team-results__row--even {
  background-color: #fafafa;
}

.team-results__sort-button {
  background: none;
  border: none;
  cursor: pointer;
  font-weight: inherit;
  color: inherit;
  display: flex;
  align-items: center;
  gap: var(--e4s-gap--small-qaz);
  padding: 0;
}

.team-results__sort-button:hover {
  color: var(--e4s-color-primary);
}

.team-results__sort-button--active {
  font-weight: 600;
}

.team-results__sort-icon {
  font-size: 0.8em;
}

.team-results__event-group {
  font-size: 0.8em;
  color: var(--e4s-color-text-secondary);
  margin-left: var(--e4s-gap--small);
}

.team-results__empty-position {
  color: var(--e4s-color-text-secondary);
}

.team-results__athlete-count {
  font-size: 0.8em;
  color: var(--e4s-color-text-secondary);
  margin-left: var(--e4s-gap--small-qaz);
}

.team-results__empty {
  text-align: center;
  padding: var(--e4s-gap--standard);
  color: var(--e4s-color-text-secondary);
}

.team-results__filters {
  margin: var(--e4s-gap--standard) 0;
  padding: var(--e4s-gap--small);
  background-color: var(--e4s-color-background-light);
  border-radius: var(--e4s-border-radius);
  border: 1px solid var(--e4s-color-border-light);
}

.team-results__select {
  padding: var(--e4s-gap--small-qaz);
  border: 1px solid var(--e4s-color-border-light);
  border-radius: var(--e4s-border-radius);
  background-color: var(--e4s-color-background);
  color: var(--e4s-color-text);
  font-size: inherit;
  min-width: 150px;
}

.team-results__select:focus {
  outline: 2px solid var(--e4s-color-primary);
  border-color: var(--e4s-color-primary);
}

.team-results__position-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  font-size: inherit;
  color: inherit;
  text-decoration: underline;
  text-decoration-color: transparent;
  transition: text-decoration-color 0.2s ease;
}

.team-results__position-button:hover {
  text-decoration-color: var(--e4s-color-primary);
}

.team-results__modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--e4s-gap--standard);
}

.team-results__modal {
  background-color: white;
  border-radius: var(--e4s-border-radius);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  max-width: 450px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  border: 1px solid var(--e4s-color-border-light);
}

.team-results__modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--e4s-gap--standard);
  background-color: var(--e4s-color-background-light);
  border-bottom: 2px solid var(--e4s-color-border-light);
  border-radius: var(--e4s-border-radius) var(--e4s-border-radius) 0 0;
}

.team-results__modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--e4s-color-text-secondary);
  padding: var(--e4s-gap--small-qaz);
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--e4s-border-radius);
  transition: all 0.2s ease;
}

.team-results__modal-close:hover {
  background-color: var(--e4s-color-background);
  color: var(--e4s-color-text);
  transform: scale(1.1);
}

.team-results__modal-content {
  padding: 0;
}

.team-results__athlete-info {
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #636262;
}

.team-results__athlete-row {
  display: flex;
  align-items: center;
  padding: var(--e4s-gap--standard);
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.team-results__athlete-row:hover {
  background-color: var(--e4s-color-background-light);
}

.team-results__athlete-row:last-child {
  border-bottom: none;
}

.team-results__athlete-row:nth-child(even) {
  background-color: rgba(0, 0, 0, 0.02);
}

.team-results__label {
  font-weight: 600;
  color: var(--e4s-color-text);
  min-width: 100px;
  font-size: 0.9em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.8;
}

.team-results__value {
  font-weight: 500;
  color: var(--e4s-color-text);
  text-align: left;
  flex: 1;
  font-size: 1em;
  word-break: break-word;
}

.team-results__date-filter {
  margin-top: var(--e4s-gap--standard);
  padding: var(--e4s-gap--small);
  background-color: var(--e4s-color-background-light);
  border-radius: var(--e4s-border-radius);
}

.team-results__row--unqualified {
  background-color: lightgray;
}

.team-results__legend-row {
  display: flex;
  align-items: center;
  gap: var(--e4s-gap--small-qaz);
  margin: var(--e4s-gap--small) 0;
}

.team-results__legend-rectangle {
  width: 16px;
  height: 16px;
  background-color: lightgray;
  border: 1px solid #ccc;
}

.team-results__legend-text {
  font-size: 0.9em;
  color: var(--e4s-color-text-secondary);
  white-space: nowrap;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .team-results__modal-overlay {
    padding: var(--e4s-gap--small);
  }

  .team-results__modal {
    max-width: none;
    width: 100%;
    margin: 0;
  }

  .team-results__position-button {
    min-height: 44px; /* iOS touch target minimum */
    min-width: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
